import { auth } from "@/auth";
import { retryWithBackoff } from "@common-ui/utilities/retry";
import {
	AnalysisCreateResponse,
	PromptResponse,
	AnalysisStatusResponse,
	AnalysisResult,
	ScribeFile,
	Prompt,
	CreateAnalysisRequest,
	PromptListResponse,
	AssistantToolsResponse,
	AnalysisSelection,
	AvailablePlatformsResponse,
	TaskSelectionResponse,
	SaveTaskSelectionRequest,
	PackResponse,
	PackListResponse,
	DryRunValidationResult,
	PackTask,
	Pack,
	PlatformInfo,
	Task,
	PackPlatformsResponse,
	AnalysisMetadata,
	TaskResultsResponse
} from "@/types/scribe";
import { Tool } from "@/types/scribe/Tools";
import { tryParsePartialJson } from "@/helpers/partialJsonParser";
import { Marked } from "marked";
import DOMPurify from "dompurify";
import type { RenderingMetadata } from "@/helpers/analysisRenderingUtils";
import { sparkyColdStartDiagnostics } from "@/services/sparkyColdStartDiagnostics";
import { noteScribeAccessError } from "@/helpers/scribeAccess";

// Isolated Marked instance — Tiptap's MarkdownManager mutates the shared `marked`
// singleton's tokenizer (e.g. task-list parsing), which corrupts report rendering.
// Do NOT replace with the named `marked` export.
const reportMarkdownParser = new Marked();

export class ApiError extends Error {
	constructor(
		message: string,
		public readonly status: number
	) {
		super(message);
		this.name = "ApiError";
	}
}

function formatValidationMessages(value: unknown): string | null {
	if (!Array.isArray(value)) return null;

	const messages = value
		.map((item: any) => {
			if (typeof item === "string") return item.trim();
			return typeof item?.message === "string" ? item.message.trim() : "";
		})
		.filter(Boolean);

	return messages.length > 0 ? messages.join("\n") : null;
}

const getUrl = (path: string) => {
	const url = window.env.VITE_SCRIBE_URL + "/api" + path;
	return url;
};

// `hasBody` decides whether a JSON content type is declared. It defaults to the method, which is
// right for every call that sends a payload, but a bodyless POST must pass `false`: declaring
// application/json with no body is malformed, and scribe-api (Fastify) rejects it with a 400
// before the route is reached.
const getHeaders = async (method: string, hasBody: boolean = !["DELETE", "GET"].includes(method)) => {
	const devToken = window.env.VITE_SCRIBE_DEV_TOKEN;
	let token: string;

	if (devToken) {
		token = devToken;
	} else {
		if (auth.runningCallback) {
			await auth.waitForCallbackToFinish();
		}
		token = await auth.getAccessToken();
	}

	const headers: Record<string, string> = {
		Authorization: "Bearer " + token
	};

	if (hasBody) {
		headers["Content-Type"] = "application/json";
	}

	return headers;
};

const buildResponseError = async (response: Response | null | undefined): Promise<Error | null> => {
	if (response != null) {
		try {
			const text = await response.text();
			try {
				const json = JSON.parse(text);
				const validationMessage = formatValidationMessages(json);
				if (validationMessage) return new ApiError(validationMessage, response.status);
				return new ApiError(json.message ?? json.error ?? text, response.status);
			} catch (e) {
				console.error("Error parsing error response", e);
				return new ApiError(text, response.status);
			}
		} catch (e) {
			console.error("Error parsing error response", e);
			return e as Error;
		}
	}
	return new Error("Unknown error");
};

// LSP-17976: every failed Scribe response passes through here, so an archived-membership 403 is
// recorded once for the Spark surfaces (useScribeAccess) instead of being swallowed per call site.
const responseToError = async (response: Response | null | undefined): Promise<Error | null> => {
	const error = await buildResponseError(response);
	noteScribeAccessError(error);
	return error;
};

const getPacks = async (): Promise<PromptListResponse> => {
	const response = await fetch(getUrl("/packs"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getPack = async (id: string): Promise<PackResponse> => {
	const response = await fetch(getUrl("/packs/" + id), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getPrompts = async (): Promise<PromptListResponse> => {
	const response = await fetch(getUrl("/prompts"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getPrompt = async (id: string): Promise<PromptResponse> => {
	const response = await fetch(getUrl("/prompts/" + id), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getAnalysis = async (id: string): Promise<AnalysisResult | null> => {
	const response = await fetch(getUrl("/analyses/" + id), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return null;
	} else {
		throw await responseToError(response);
	}
};

const getAnalysisStatus = async (
	externalSource: string,
	externalType: string,
	externalId: string
): Promise<AnalysisStatusResponse | null> => {
	const routeType = externalType === "project" || externalType === "conversation" ? externalType : "other";
	const traceFirstStatus = sparkyColdStartDiagnostics.claim("scribe_status_complete");
	let traceAttemptAvailable = traceFirstStatus;
	const requestContext: { routeType: "project" | "conversation" | "other"; status?: number } = { routeType };
	const completeTiming = traceFirstStatus ? sparkyColdStartDiagnostics.startPhase("scribe_status_complete", requestContext) : null;
	try {
		const result = await retryWithBackoff(
			async () => {
				const traceThisAttempt = traceAttemptAvailable;
				const headers = await getHeaders("GET");
				traceAttemptAvailable = false;
				const headersTiming = traceThisAttempt
					? sparkyColdStartDiagnostics.startPhase("scribe_status_headers", requestContext)
					: null;
				let response: Response;
				try {
					response = await fetch(getUrl("/analyses/status/external/" + externalSource + "/" + externalType + "/" + externalId), {
						method: "GET",
						headers
					});
					requestContext.status = response.status;
					headersTiming?.finish("success");
				} catch (error) {
					headersTiming?.finish("transient_failure");
					throw error;
				}
				if (response != null && response.ok) {
					const bodyTiming = traceThisAttempt
						? sparkyColdStartDiagnostics.startPhase("scribe_status_body", requestContext)
						: null;
					try {
						const result = await response.json();
						bodyTiming?.finish("success");
						return result;
					} catch (error) {
						bodyTiming?.finish("transient_failure");
						throw error;
					}
				} else if (response != null && response.status == 404) {
					return null;
				} else {
					throw await responseToError(response);
				}
			},
			1,
			500
		);
		completeTiming?.finish("success");
		return result;
	} catch (error) {
		completeTiming?.finish("transient_failure");
		throw error;
	}
};

const markdownToHtml = async (markdown: string) => {
	// Clean up problematic whitespace patterns in HTML tables that confuse the markdown parser
	const cleanedResult = markdown.replace(/>\s*(\n|\\n)*\s*</g, "><"); // Remove whitespace between HTML tags only

	const html = await reportMarkdownParser.parse(cleanedResult);
	return DOMPurify.sanitize(html);
};

const createAnalysis = async (request: CreateAnalysisRequest): Promise<AnalysisCreateResponse> => {
	request.shareWith = "default"; // always share with the default team
	const response = await fetch(getUrl("/analyses"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(request)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

// Preflight analysis: lets the server resolve selection and auth without starting a job
// Returns raw status and parsed json for caller to decide next steps
const preflightAnalysis = async (
	request: Omit<CreateAnalysisRequest, "dryRun"> & { selection?: AnalysisSelection }
): Promise<{ status: number; ok: boolean; json: unknown }> => {
	const body = { ...request, dryRun: true } as CreateAnalysisRequest;
	const response = await fetch(getUrl("/analyses"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(body)
	});
	let json: unknown = null;
	try {
		const text = await response.text();
		json = text ? JSON.parse(text) : null;
	} catch (_) {
		json = null;
	}
	return { status: response.status, ok: response.ok, json };
};

// Comprehensive dry-run validation that checks platform auth and task selection
const validateDryRun = async (packId: string): Promise<DryRunValidationResult> => {
	try {
		// First, get the pack details to understand what tasks are available
		const packResponse = await getPack(packId);
		const pack = packResponse.pack;

		if (!pack) {
			throw new Error("Pack not found");
		}

		// Get platform tasks from the pack
		const platformTasks = (pack as Pack).tasks?.filter((task: PackTask) => task.type === "platform") || [];

		if (platformTasks.length === 0) {
			// No platform tasks, so no authentication needed
			return {
				isValid: true,
				needsAuth: false,
				needsSelection: false,
				platforms: [],
				unauthenticatedPlatforms: [],
				tasks: [],
				taskSelection: null
			};
		}

		// Get available platforms for the current user
		const availablePlatforms = await getAvailablePlatforms();

		// Check which platforms are required and their auth status
		const requiredPlatforms = platformTasks
			.map((task: PackTask) => task.platform)
			.filter((platform): platform is string => Boolean(platform));
		const platformAuthStatus = requiredPlatforms.map((platformCode: string) => {
			const platform = availablePlatforms.platforms.find((p: PlatformInfo) => p.codeName === platformCode);
			return {
				codeName: platformCode,
				productName: platform?.productName || platformCode,
				authenticationStatus: platform?.authenticationStatus || "NotAuthenticated",
				required: true
			};
		});

		const unauthenticatedPlatforms = platformAuthStatus.filter((p) => p.authenticationStatus !== "Authenticated") as Array<{
			codeName: string;
			productName: string;
			authenticationStatus: "NotAuthenticated" | "AuthenticatedButFailing";
			required: boolean;
		}>;

		// Check if user has task selection saved
		const taskSelectionResponse = await getTaskSelection(packId);
		const taskSelection = taskSelectionResponse?.selection || null;

		return {
			isValid: unauthenticatedPlatforms.length === 0 && taskSelection !== null,
			needsAuth: unauthenticatedPlatforms.length > 0,
			needsSelection: taskSelection === null,
			platforms: platformAuthStatus,
			unauthenticatedPlatforms,
			tasks: platformTasks,
			taskSelection
		};
	} catch (error) {
		console.error("Dry run validation failed:", error);
		throw error;
	}
};

const deletePrompt = async (id: string) => {
	const response = await fetch(getUrl("/prompts/" + id), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const hidePrompt = async (id: string) => {
	const response = await fetch(getUrl("/prompts/" + id + "/hide"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({
			id: id
		})
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const createPrompt = async (prompt: Prompt) => {
	const response = await fetch(getUrl("/prompts"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(prompt)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const updatePrompt = async (prompt: Prompt) => {
	const response = await fetch(getUrl("/prompts/" + prompt.id), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify(prompt)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getAllAnalyses = async (page: number, pageSize: number): Promise<{ total: number; data: AnalysisResult[] }> => {
	const status = ["COMPLETED", "RUNNING"];
	const response = await fetch(
		getUrl("/analyses?limit=" + pageSize + "&offset=" + (page - 1) * pageSize) + "&status=" + status.join(","),
		{
			method: "GET",
			headers: await getHeaders("GET")
		}
	);
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return { total: 0, data: [] };
	} else {
		throw await responseToError(response);
	}
};

const getAnalysesCount = async (): Promise<number> => {
	const response = await fetch(getUrl("/analyses?limit=1&offset=0&sortBy=createdAt&sortOrder=desc"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		const result = await response.json();
		return Number(result?.totalCount ?? result?.total ?? 0);
	} else if (response != null && response.status == 404) {
		return 0;
	} else {
		throw await responseToError(response);
	}
};

const getAnalyses = async (
	externalSource: string,
	externalType: string,
	externalId: string
): Promise<{ total: number; data: AnalysisResult[] }> => {
	const response = await fetch(getUrl("/analyses/results/external/" + externalSource + "/" + externalType + "/" + externalId), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return { total: 0, data: [] };
	} else {
		throw await responseToError(response);
	}
};

const getAnalysesBulk = async (
	externalSource: string,
	externalType: string,
	externalIds: string[]
): Promise<{ results: { total: number; externalId: string; data: AnalysisResult[] }[] }> => {
	const identifiers = externalIds.map((id) => ({
		externalSource: externalSource,
		externalType: externalType,
		externalId: id
	}));

	const response = await fetch(getUrl("/analyses/results/external/bulk"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({ identifiers })
	});

	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return { results: [] };
	} else {
		throw await responseToError(response);
	}
};

const deleteAnalysis = async (id: string) => {
	const response = await fetch(getUrl("/analyses/" + id), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});

	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const getAssistantTools = async (): Promise<AssistantToolsResponse> => {
	const response = await fetch(getUrl("/assistant/tools"), {
		method: "GET",
		headers: await getHeaders("GET")
	});

	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const createAssistantAutomation = async (automationPrompt: string, mediaPrompt: string, tools: Tool[] = []) => {
	const response = await fetch(getUrl("/assistant/automations"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({
			automationPrompt: automationPrompt,
			mediaPrompt: mediaPrompt,
			tools: tools
		})
	});

	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

// Get available platforms for the current user. `allowStale` accepts the user's last good list
// while Exchange is unavailable; leave it off wherever the result decides whether a login worked.
const getAvailablePlatforms = async (options?: { allowStale?: boolean }): Promise<AvailablePlatformsResponse> => {
	const response = await fetch(getUrl(options?.allowStale ? "/tasks/platforms?allowStale=true" : "/tasks/platforms"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

// Save task selection for a pack
const saveTaskSelection = async (packId: string, taskIds: string[], selectionState?: string): Promise<TaskSelectionResponse> => {
	const response = await fetch(getUrl("/packs/" + packId + "/task-selection"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({
			taskIds,
			selectionState
		})
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

// Get task selection for a pack
const getTaskSelection = async (packId: string): Promise<TaskSelectionResponse | null> => {
	const response = await fetch(getUrl("/packs/" + packId + "/task-selection"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return null;
	} else {
		throw await responseToError(response);
	}
};

// Delete task selection for a pack
const deleteTaskSelection = async (packId: string): Promise<void> => {
	const response = await fetch(getUrl("/packs/" + packId + "/task-selection"), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response != null && response.ok) {
		return;
	} else if (response != null && response.status == 404) {
		// Already deleted, that's fine
		return;
	} else {
		throw await responseToError(response);
	}
};

const getPackPlatforms = async (packId: string): Promise<{ platforms: any[] }> => {
	const response = await fetch(getUrl("/packs/" + packId + "/platforms"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		const data: PackPlatformsResponse = await response.json();
		// Transform the response to match the expected structure
		const transformedPlatforms = data.platforms.map((platform) => ({
			platformCodeName: platform.codeName,
			platformName: platform.productName,
			productName: platform.productName,
			vendorName: platform.vendorName,
			authenticationStatus: platform.authenticationStatus,
			automationIds: [] // No longer using automation IDs
		}));
		return { platforms: transformedPlatforms };
	} else {
		throw await responseToError(response);
	}
};

// ──────────────────────────────────────────────────────────────────────────────
// Platform auth (login / logout) — routed through scribe, NOT Exchange.
//
// The realtime apps (contact-async / contact-live) already do platform auth through
// scribe via the realtime SDK engine (getPlatformLoginUrl / logoutFromPlatform). These
// two helpers give the contact-admin run/queue/template flows the same scribe-proxied
// transport so the platform-login UX is unified across every Spark entry point and admin
// no longer talks to the legacy Exchange service for platform auth.
//
const PLATFORM_AUTH_BASE = "/platforms";

const getPlatformLoginUrl = (codeName: string, redirectUri: string, organizationId?: string): string => {
	const params = new URLSearchParams();
	params.set("redirect_uri", redirectUri);
	if (organizationId) {
		params.set("organizationId", organizationId);
	}
	return getUrl(`${PLATFORM_AUTH_BASE}/${encodeURIComponent(codeName)}/oauth/start`) + `?${params.toString()}`;
};

const logoutPlatform = async (codeName: string, organizationId?: string): Promise<{ success: boolean }> => {
	const params = new URLSearchParams();
	if (organizationId) {
		params.set("organizationId", organizationId);
	}
	const query = params.toString();
	const response = await fetch(
		getUrl(`${PLATFORM_AUTH_BASE}/${encodeURIComponent(codeName)}/oauth/logout`) + (query ? `?${query}` : ""),
		{
			// The endpoint takes no body — identity comes from the bearer token and the URL — so no
			// JSON content type is declared either.
			method: "POST",
			headers: await getHeaders("POST", false)
		}
	);
	if (response != null && response.ok) {
		return { success: true };
	}
	throw await responseToError(response);
};

const getAnalysisTaskResults = async (analysisId: string): Promise<TaskResultsResponse> => {
	const response = await fetch(getUrl("/analyses/" + analysisId + "/task-results"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return { data: [], total: 0 };
	} else {
		throw await responseToError(response);
	}
};

const cancelAnalysis = async (id: string): Promise<void> => {
	const response = await fetch(getUrl("/analyses/" + id + "/cancel"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({})
	});
	if (response != null && response.ok) {
		return;
	} else {
		throw await responseToError(response);
	}
};

// scribe publish endpoint is idempotent — it preserves an existing token on re-publish,
// so calling it each time the share dialog opens is safe. perm "read" == "Anyone with the link".
const publishAnalysis = async (id: string): Promise<{ publicToken: string; publicPerm: string; publicUrl: string }> => {
	const response = await fetch(getUrl("/analyses/" + id + "/publish"), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify({ perm: "read" })
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const finalizeRealtimeAnalysis = async (sessionId: string, analysisId: string): Promise<void> => {
	const response = await fetch(getUrl("/realtime/admin/finalize"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({
			sessionId,
			analysisId
		})
	});
	if (response != null && response.ok) {
		return;
	} else {
		throw await responseToError(response);
	}
};

export interface ReviewResult {
	score: number;
	analysis: string;
	suggestedPrompt: string | null;
	suggestedScore: number | null;
	selectedTools: any[];
}

const reviewPrompt = async (
	prompt: string,
	connectionId: string,
	onProgress?: (status: string, message?: string) => void,
	onPartialData?: (partialData: Partial<ReviewResult>) => void,
	signal?: AbortSignal
): Promise<ReviewResult> => {
	const headers = await getHeaders("POST");
	headers["X-SSE-Connection-Id"] = connectionId;

	const response = await fetch(getUrl("/assistant/review-agent-task"), {
		method: "POST",
		headers,
		body: JSON.stringify({ prompt, type: "media" }),
		signal
	});

	if (!response.ok) {
		const err = await responseToError(response);
		throw err;
	}

	if (response.status !== 202) {
		throw new Error(`Unexpected response status: ${response.status}`);
	}

	// Correlation id issued by the server and stamped on every assistant:* SSE
	// event for THIS review. Without it, a review abandoned mid-flight (modal
	// closed, prompt edited, Review clicked again) keeps running server-side and
	// its completion would resolve the NEXT review with the old prompt's result.
	// Older servers omit it — then we fall back to unfiltered (legacy) matching.
	//
	// The id lives in the 202 body, but the review's first events can arrive
	// over SSE before the body is parsed (the server starts the review as soon
	// as it replies). Listeners register immediately and BUFFER events until
	// the ack is parsed, then replay them through the filter. `undefined` = ack
	// not parsed yet; `null` = parsed, no taskId (legacy server).
	let taskId: string | null | undefined = undefined;
	let loggedDroppedEvent = false;
	const matchesThisReview = (eventData: { taskId?: unknown }): boolean => {
		const matches = !taskId || eventData.taskId === taskId;
		if (!matches && !loggedDroppedEvent) {
			loggedDroppedEvent = true;
			console.warn(`[reviewPrompt] Dropping assistant SSE event from another review (expected taskId "${taskId}", received "${String(eventData.taskId ?? "none")}")`);
		}
		return matches;
	};

	return new Promise((resolve, reject) => {
		let timeout: ReturnType<typeof setTimeout> | null = null;
		const INITIAL_TIMEOUT = 300000; // 5 minutes
		const TOKEN_TIMEOUT = 120000; // 120 seconds per token

		let accumulatedJson = "";

		const resetTimeout = (useTokenTimeout = false) => {
			if (timeout) {
				clearTimeout(timeout);
			}
			const duration = useTokenTimeout ? TOKEN_TIMEOUT : INITIAL_TIMEOUT;
			timeout = setTimeout(() => {
				cleanup();
				reject(
					new Error(
						"Review timed out. No response was received for more than " + (useTokenTimeout ? "120 seconds" : "5 minutes") + "."
					)
				);
			}, duration);
		};

		resetTimeout();

		const handleToken = (event: Event) => {
			const eventData = (event as CustomEvent).detail;
			if (!matchesThisReview(eventData)) return;
			if (eventData.type === "assistant:token" && eventData.content) {
				resetTimeout(true);
				accumulatedJson += eventData.content;
				const partialData = tryParsePartialJson(accumulatedJson);
				if (partialData && onPartialData) {
					onPartialData(partialData);
				}
			}
		};

		const handleComplete = (event: Event) => {
			const eventData = (event as CustomEvent).detail;
			if (!matchesThisReview(eventData)) return;
			if (eventData.type === "assistant:complete") {
				cleanup();
				resolve({
					score: eventData.score,
					analysis: eventData.analysis,
					suggestedPrompt: eventData.suggestedPrompt,
					suggestedScore: eventData.suggestedScore,
					selectedTools: eventData.selectedTools || []
				});
			}
		};

		const handleError = (event: Event) => {
			const eventData = (event as CustomEvent).detail;
			if (!matchesThisReview(eventData)) return;
			cleanup();
			reject(new Error(eventData.message || "Unknown error occurred"));
		};

		const handleProgress = (event: Event) => {
			const eventData = (event as CustomEvent).detail;
			if (!matchesThisReview(eventData)) return;
			resetTimeout(true);
			if (onProgress && eventData.status) {
				onProgress(eventData.status, eventData.message);
			}
		};

		// The SSE connection was torn down and re-bound (org switch): this
		// review's remaining events are published to the OLD org's connections
		// and can never reach this tab — fail fast instead of hanging to timeout.
		const handleConnectionRebound = () => {
			cleanup();
			reject(new Error("Your organization changed while Sparky was reviewing. Please run the review again."));
		};

		// Register listeners BEFORE parsing the 202 body — its first events can
		// beat the body read. Until the taskId is known, buffer, then replay
		// through the filter. (The rebound handler is not gated: a torn-down
		// connection dooms this review regardless of correlation.)
		// Once the review settles (complete/error/abort/timeout/rebound), the
		// deferred ack parse must not replay buffered events, re-arm timers, or
		// warn about a body read failed by the abort — `finished` gates all of it.
		let finished = false;
		const bufferedEvents: Array<{ handler: (event: Event) => void; event: Event }> = [];
		const gated = (handler: (event: Event) => void) => (event: Event) => {
			if (taskId === undefined) {
				bufferedEvents.push({ handler, event });
				return;
			}
			handler(event);
		};
		const gatedComplete = gated(handleComplete);
		const gatedError = gated(handleError);
		const gatedProgress = gated(handleProgress);
		const gatedToken = gated(handleToken);

		const cleanup = () => {
			finished = true;
			bufferedEvents.length = 0;
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}
			window.removeEventListener("assistant:complete", gatedComplete);
			window.removeEventListener("assistant:error", gatedError);
			window.removeEventListener("assistant:progress", gatedProgress);
			window.removeEventListener("assistant:token", gatedToken);
			window.removeEventListener("scribe:connection-rebound", handleConnectionRebound);
		};

		window.addEventListener("assistant:complete", gatedComplete);
		window.addEventListener("assistant:error", gatedError);
		window.addEventListener("assistant:progress", gatedProgress);
		window.addEventListener("assistant:token", gatedToken);
		window.addEventListener("scribe:connection-rebound", handleConnectionRebound);

		void response
			.json()
			.catch((err: unknown) => {
				// Distinct from the legitimate legacy no-taskId path: a NEW server
				// sent a body we couldn't parse, so correlation is off for this run.
				// (Silent when the review already settled — an abort fails the body
				// read too, and that's expected, not a correlation problem.)
				if (!finished) {
					console.warn("[reviewPrompt] Could not parse review-agent-task 202 body — taskId correlation disabled for this review", err);
				}
				return null;
			})
			.then((ack: { taskId?: string } | null) => {
				if (finished) return;
				taskId = typeof ack?.taskId === "string" && ack.taskId.length > 0 ? ack.taskId : null;
				for (const buffered of bufferedEvents.splice(0)) {
					// A replayed completion/error settles the review mid-loop —
					// nothing after it may run.
					if (finished) break;
					buffered.handler(buffered.event);
				}
			});

		if (signal) {
			if (signal.aborted) {
				cleanup();
				reject(new DOMException("The operation was aborted.", "AbortError"));
				return;
			}
			signal.addEventListener(
				"abort",
				() => {
					cleanup();
					reject(new DOMException("The operation was aborted.", "AbortError"));
				},
				{ once: true }
			);
		}
	});
};

const updateAnalysis = async (id: string, { result, title }: { result?: unknown; title?: string }): Promise<void> => {
	const response = await fetch(getUrl("/analyses/" + id), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify({ result, title })
	});
	if (response != null && response.ok) {
		return;
	} else {
		throw await responseToError(response);
	}
};

const getRawAnalysis = async (
	id: string,
	options?: { includeReferences?: boolean; resolveReferences?: boolean }
): Promise<(AnalysisResult & { rendering?: RenderingMetadata }) | null> => {
	const params = new URLSearchParams({ raw: "true" });
	if (options?.includeReferences) params.set("includeReferences", "true");
	if (options?.resolveReferences) params.set("resolveReferences", "true");
	const response = await fetch(getUrl("/analyses/" + id + "?" + params.toString()), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else if (response != null && response.status == 404) {
		return null;
	} else {
		throw await responseToError(response);
	}
};

export type ReportReviewRating = 5 | 3 | 1;

export interface ReportReviewOptions {
	ratings: Array<{
		value: ReportReviewRating;
		label: string;
	}>;
	tagsByRating?: Partial<Record<ReportReviewRating, string[]>>;
}

export interface ReportReview {
	id: string;
	analysisId: string;
	userId: string;
	rating: number;
	tags?: string[] | null;
	note?: string | null;
	feedback?: string | null;
	createdAt?: string;
	updatedAt?: string;
	user?: {
		id?: string | null;
		email?: string | null;
		firstName?: string | null;
		lastName?: string | null;
	};
}

export interface ReportReviewListResponse {
	data: ReportReview[];
	total: number;
	averageRating: number | null;
	reviewOptions?: ReportReviewOptions;
}

const submitReview = async (
	analysisId: string,
	rating: ReportReviewRating,
	tags?: string[] | null,
	note?: string | null
): Promise<void> => {
	const response = await fetch(getUrl("/analyses/" + analysisId + "/review"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({
			rating,
			tags: tags ?? null,
			feedback: note || null
		})
	});
	if (response != null && response.ok) {
		return;
	} else if (response != null && response.status === 409) {
		throw new ApiError("You have already reviewed this report.", 409);
	} else {
		throw await responseToError(response);
	}
};

const getReviews = async (analysisId: string): Promise<ReportReviewListResponse> => {
	const response = await fetch(getUrl("/analyses/" + analysisId + "/reviews"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

const hasReviewed = async (analysisId: string): Promise<boolean> => {
	const response = await fetch(getUrl("/analyses/" + analysisId + "/has-reviewed"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		const result: { hasReviewed: boolean } = await response.json();
		return result.hasReviewed;
	} else {
		throw await responseToError(response);
	}
};

const getPackReviews = async (packId: string): Promise<{ data: unknown[]; total: number; averageRating: number | null }> => {
	const response = await fetch(getUrl("/packs/" + packId + "/reviews"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

type PdfTextStyle = {
	bold?: boolean;
	italic?: boolean;
	fontSize?: number;
	color?: string;
};

type PdfHeaderRequest = {
	leftTitle?: string;
	leftTitleStyle?: PdfTextStyle;
	leftTitleLogoUrl?: string;
	leftTitleLogoAlt?: string;
	leftInfo?: string[];
	leftInfoStyle?: PdfTextStyle;
	rightTitle?: string;
	rightTitleStyle?: PdfTextStyle;
	rightTitleLogoUrl?: string;
	rightTitleLogoAlt?: string;
	rightInfo?: string[];
	rightInfoStyle?: PdfTextStyle;
};

const pdfHeaderRequestKeys = [
	"leftTitle",
	"leftTitleStyle",
	"leftTitleLogoUrl",
	"leftTitleLogoAlt",
	"leftInfo",
	"leftInfoStyle",
	"rightTitle",
	"rightTitleStyle",
	"rightTitleLogoUrl",
	"rightTitleLogoAlt",
	"rightInfo",
	"rightInfoStyle"
] as const satisfies readonly (keyof PdfHeaderRequest)[];

type PdfFooterSlotRequest = {
	text?: string;
	html?: string;
	style?: PdfTextStyle;
};

type PdfFooterRequest = {
	show?: boolean;
	left?: PdfFooterSlotRequest;
	center?: PdfFooterSlotRequest;
	right?: PdfFooterSlotRequest;
	defaultStyle?: PdfTextStyle;
};

const exportPdf = async (
	analysisId: string,
	header: PdfHeaderRequest = {},
	options: { connectionId?: string | null } = {},
	footer?: PdfFooterRequest
): Promise<{ jobId: string; message: string }> => {
	// scribe-api expects `{ headerConfig, footerConfig }`;
	// flat fields are stripped server-side.
	const headerConfig = pdfHeaderRequestKeys.reduce<Partial<PdfHeaderRequest>>((config, key) => {
		const value = header[key];
		if (Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== "") {
			return { ...config, [key]: value };
		}
		return config;
	}, {});
	const body = {
		...(Object.keys(headerConfig).length > 0 ? { headerConfig } : {}),
		...(footer ? { footerConfig: footer } : {})
	};
	// Scope pdf-ready to the originating SSE connection; otherwise scribe-api
	// broadcasts to every tab the user has open and each opens its own download.
	const headers = await getHeaders("POST");
	if (options.connectionId) {
		headers["X-SSE-Connection-Id"] = options.connectionId;
	}
	const response = await fetch(getUrl("/analyses/" + analysisId + "/export-pdf"), {
		method: "POST",
		headers,
		body: JSON.stringify(body)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export {
	getPack,
	getPacks,
	deletePrompt,
	hidePrompt,
	createPrompt,
	updatePrompt,
	getPrompts,
	getPrompt,
	getAnalysisStatus,
	getAnalyses,
	getAnalysesBulk,
	getAnalysis,
	getAllAnalyses,
	getAnalysesCount,
	createAnalysis,
	deleteAnalysis,
	getAssistantTools,
	createAssistantAutomation,
	getPackPlatforms,
	preflightAnalysis,
	markdownToHtml,
	getAvailablePlatforms,
	getPlatformLoginUrl,
	logoutPlatform,
	saveTaskSelection,
	getTaskSelection,
	deleteTaskSelection,
	validateDryRun,
	getAnalysisTaskResults,
	cancelAnalysis,
	publishAnalysis,
	finalizeRealtimeAnalysis,
	reviewPrompt,
	updateAnalysis,
	getRawAnalysis,
	submitReview,
	getReviews,
	hasReviewed,
	getPackReviews,
	exportPdf
};
